import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./App.css";

const API = "http://192.168.100.254:5001";

const GENRE_COLORS = [
  "#c17f3a","#8b4513","#6b8e23","#4682b4","#9370db",
  "#cd5c5c","#20b2aa","#d2691e","#708090","#b8860b"
];
function genreColor(genre) {
  let h = 0;
  for (let c of (genre || "")) h = (h * 31 + c.charCodeAt(0)) & 0xffffff;
  return GENRE_COLORS[h % GENRE_COLORS.length];
}

export default function App() {
  const [books, setBooks] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [genreFilter, setGenreFilter] = useState("");
  const [selectedBook, setSelectedBook] = useState(null);
  const [highlightedPositions, setHighlightedPositions] = useState([]);
  const [selectedPos, setSelectedPos] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [form, setForm] = useState({ title: "", author: "", release_date: "", genre: "" });
  const shelfRef = useRef(null);

  useEffect(() => { fetchBooks(); }, []);
  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const fetchBooks = async () => {
    const res = await axios.get(`${API}/books`);
    setBooks(res.data);
    setFiltered(res.data);
  };

  const genres = [...new Set(books.map(b => b.genre).filter(Boolean))].sort();

  const applyFilters = (list, sort, genre) => {
    let out = genre ? list.filter(b => (b.genre || "").toLowerCase().includes(genre.toLowerCase())) : [...list];
    if (sort === "newest") out.sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
    else if (sort === "oldest") out.sort((a, b) => new Date(a.release_date) - new Date(b.release_date));
    else if (sort === "az") out.sort((a, b) => a.title.localeCompare(b.title));
    return out;
  };

  const handleSort = (e) => {
    setSortOrder(e.target.value);
    setFiltered(applyFilters(books, e.target.value, genreFilter));
  };

  const handleGenre = (e) => {
    setGenreFilter(e.target.value);
    setFiltered(applyFilters(books, sortOrder, e.target.value));
  };

  const doSearch = async () => {
    if (!search.trim()) {
      await fetchBooks();
      setHighlightedPositions([]);
      setSelectedBook(null);
      return;
    }
    const res = await axios.get(`${API}/search/${encodeURIComponent(search)}`);
    const data = res.data;
    setFiltered(data);
    const positions = data.map(b => {
      try { return b.position ? JSON.parse(b.position) : null; } catch { return null; }
    }).filter(Boolean);
    setHighlightedPositions(positions);
    if (data.length === 1 && data[0].position) selectBook(data[0]);
  };

  const clearSearch = () => {
    setSearch("");
    setFiltered(applyFilters(books, sortOrder, genreFilter));
    setHighlightedPositions([]);
    setSelectedBook(null);
  };

  const selectBook = (book) => {
    setSelectedBook(book);
    if (book.position) {
      try { setHighlightedPositions([JSON.parse(book.position)]); } catch {}
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShelfClick = (e) => {
    if (!showAdmin) return;
    const img = shelfRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    setSelectedPos({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
  };

  const submitBook = async () => {
    if (!form.title || !form.author || !form.release_date || !form.genre || !selectedPos) {
      alert("Please fill all fields and click the shelf to set a position.");
      return;
    }
    await axios.post(`${API}/addBook`, { ...form, position: JSON.stringify(selectedPos) });
    setForm({ title: "", author: "", release_date: "", genre: "" });
    setSelectedPos(null);
    setShowAdmin(false);
    fetchBooks();
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <div className="eyebrow">Personal Collection</div>
          <h1>Mein <em>Bücherregal</em></h1>
          <p className="subtitle">A library of stories, worlds &amp; ideas</p>
        </div>
        <div className="toggle-wrap">
          <span className="toggle-label">{darkMode ? "Moody" : "Cozy"}</span>
          <label className="toggle" title="Switch theme">
            <input type="checkbox" checked={darkMode} onChange={e => setDarkMode(e.target.checked)} />
            <div className="toggle-track">
              <span className="icon-sun">☀</span>
              <span className="icon-moon">☾</span>
            </div>
            <div className="toggle-thumb" />
          </label>
        </div>
      </header>

      {/* Search */}
      <div className="search-row">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input
            className="text-input"
            placeholder="Search by title, author or genre…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && doSearch()}
          />
        </div>
        <button className="btn btn-primary" onClick={doSearch}>Search</button>
        <button className="btn btn-ghost" onClick={clearSearch}>Clear</button>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <span className="filter-label">Sort:</span>
        <select className="filter-select" value={sortOrder} onChange={handleSort}>
          <option value="">— none —</option>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="az">A → Z</option>
        </select>
        <span className="filter-label">Genre:</span>
        <select className="filter-select" value={genreFilter} onChange={handleGenre}>
          <option value="">All genres</option>
          {genres.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      <div className="divider" />

      {/* Shelf image */}
      <div className="shelf-section">
        <div className="shelf-label">Bookshelf Map</div>
        <div className="shelf-frame" onClick={handleShelfClick}
          style={{ cursor: showAdmin ? "crosshair" : "default" }}>
          <img ref={shelfRef} src="/Buecherwand.png" alt="Bookshelf" className="shelf-img" />
          <div className="shelf-overlay">
            {showAdmin && selectedPos && (
              <div className="pin pin-select"
                style={{ left: `${selectedPos.x * 100}%`, top: `${selectedPos.y * 100}%` }} />
            )}
            {highlightedPositions.map((pos, i) => (
              <div key={i} className="pin pin-found"
                style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%` }} />
            ))}
          </div>
          {showAdmin && <div className="shelf-hint">Click to place the book's position</div>}
        </div>
      </div>

      {/* Selected book callout */}
      {selectedBook && (
        <div className="book-callout">
          <div className="callout-icon">📖</div>
          <div className="callout-info">
            <div className="callout-title">{selectedBook.title}</div>
            <div className="callout-meta">
              {selectedBook.author} · {selectedBook.genre} · {(selectedBook.release_date || "").slice(0, 4)}
            </div>
          </div>
          <button className="callout-close"
            onClick={() => { setSelectedBook(null); setHighlightedPositions([]); }}>✕</button>
        </div>
      )}

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-num">{books.length}</div>
          <div className="stat-lbl">Total books</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{filtered.length}</div>
          <div className="stat-lbl">Showing</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{genres.length}</div>
          <div className="stat-lbl">Genres</div>
        </div>
      </div>

      {/* Admin toggle */}
      <div className="admin-toggle-row">
        <button className="btn btn-ghost btn-sm"
          onClick={() => { setShowAdmin(v => !v); setSelectedPos(null); }}>
          {showAdmin ? "✕ Cancel" : "＋ Add a book"}
        </button>
      </div>

      {/* Admin panel */}
      {showAdmin && (
        <div className="admin-panel">
          <h3>Add to the collection</h3>
          <div className="form-grid">
            {[
              { key: "title", label: "Title", placeholder: "The Name of the Rose", type: "text" },
              { key: "author", label: "Author", placeholder: "Umberto Eco", type: "text" },
              { key: "genre", label: "Genre", placeholder: "Novel, Thriller…", type: "text" },
              { key: "release_date", label: "Release Date", placeholder: "", type: "date" },
            ].map(f => (
              <div key={f.key} className="form-group">
                <label className="form-label">{f.label}</label>
                <input className="form-input" type={f.type} placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <p className="pos-hint">
            {selectedPos
              ? <span className="pos-set">✓ Position set ({(selectedPos.x * 100).toFixed(1)}%, {(selectedPos.y * 100).toFixed(1)}%)</span>
              : "→ Click the shelf image above to mark the book's position"}
          </p>
          <div className="form-actions">
            <button className="btn btn-primary btn-sm" onClick={submitBook}>Add book</button>
          </div>
        </div>
      )}

      {/* Book list */}
      <div className="books-header">
        <h2>The Collection</h2>
      </div>

      {filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">📭</div>
          <p>No books found for that search.</p>
        </div>
      ) : (
        <ul className="book-list">
          {filtered.map(book => (
            <li key={book.id}
              className={`book-item${selectedBook && selectedBook.id === book.id ? " active" : ""}`}
              onClick={() => selectBook(book)}>
              <div className="book-spine" style={{ background: genreColor(book.genre) }} />
              <div className="book-item-info">
                <div className="book-title">{book.title}</div>
                <div className="book-author">{book.author}</div>
              </div>
              <span className="book-tag">{book.genre}</span>
              <span className="book-year">{(book.release_date || "").slice(0, 4)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
